import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  AgentVerificationCheck,
  AutoFraudCheck,
  ComplianceCheck,
  ValidationRequestCheck
} from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { Flex, Loader } from '@percent/lemonade'
import { AcceptanceEmailCopy } from './acceptanceEmailCopy/AcceptanceEmailCopy'
import style from './AddNewProgram.module.scss'
import { AddNewProgramFormModel, AddNewProgramProps } from './AddNewProgram.types'
import { AddNewProgramHeader } from './addNewProgramHeader/AddNewProgramHeader'
import { Configuration } from './configuration/Configuration'
import { Other } from './other/Other'
import { PartnerFields } from './partnerFields/PartnerFields'
import { validationSchema } from './validationSchema'

export function AddNewProgram({
  onAddNewProgram,
  isSaving,
  isLoadingProgram,
  programData
}: Readonly<AddNewProgramProps>) {
  const { t } = useTranslation()

  const defaultValues = useMemo(() => {
    if (programData) {
      return {
        programName: programData.name ?? '',
        primaryColor: programData.primaryColor ?? '',
        programWebsite: programData.programWebsite ?? '',
        shouldSendOutcomeEmails: programData.shouldSendOutcomeEmail,
        acceptanceEmailCopy: programData.acceptanceEmailBody ?? '',
        rejectionEmailCopy: programData.rejectionEmailBody ?? '',
        allowedOrganisationTypes: programData.configuration.allowedOrganisations.types,
        disallowedOrganisations: programData.configuration.disallowedOrganisations.relationships.map(
          ({ type, relationship }) => ({
            type,
            relationship
          })
        ),
        isDynamicValidationSubmissionStatus: programData.configuration.isStatusDynamic,
        validationRequestCheck: programData.configuration.checks.find(
          check => check.type === 'validation_request'
        ) as ValidationRequestCheck,
        identityChecks: (
          programData.configuration.checks.find(check => check.type === 'agent_verification') as AgentVerificationCheck
        )?.identityChecks,
        associationChecks: (
          programData.configuration.checks.find(check => check.type === 'agent_verification') as AgentVerificationCheck
        )?.associationChecks,
        isStatusDynamic: (
          programData.configuration.checks.find(check => check.type === 'agent_verification') as AgentVerificationCheck
        )?.isStatusDynamic,
        flags: (programData.configuration.checks.find(check => check.type === 'compliance') as ComplianceCheck)?.flags,
        failingFlags: (programData.configuration.checks.find(check => check.type === 'compliance') as ComplianceCheck)
          ?.failingFlags,
        fraudCheck: programData.configuration.checks.find(
          (check): check is AutoFraudCheck => check.type === 'fraud_check'
        ),
        partnerFields: programData.partnerFields,
        isEligibility: !!programData.configuration.checks.find(check => check.type === 'eligibility')
      }
    }

    return {
      programName: '',
      primaryColor: '',
      programWebsite: '',
      shouldSendOutcomeEmails: false,
      acceptanceEmailCopy: '',
      rejectionEmailCopy: '',
      allowedOrganisationTypes: [],
      disallowedOrganisations: [],
      isStatusDynamic: false,
      isDynamicValidationSubmissionStatus: false
    }
  }, [programData])

  const formMethods = useForm<AddNewProgramFormModel>({
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema(t)),
    mode: 'onChange',
    defaultValues
  })

  useEffect(() => {
    if (programData) {
      formMethods.reset(defaultValues)
    }
  }, [defaultValues, formMethods, programData])

  const { handleSubmit } = formMethods

  const handleSave = handleSubmit(formValue => {
    onAddNewProgram(formValue)
  })

  return (
    <FormProvider {...formMethods}>
      <div className={style.container}>
        <AddNewProgramHeader onSave={handleSave} isSaving={isSaving} isDuplicate={!!programData} />
        <Flex direction="column" gap={16} className={style.content}>
          {isLoadingProgram && <Loader />}
          <Configuration />
          <AcceptanceEmailCopy />
          <Other />
          <PartnerFields />
        </Flex>
      </div>
    </FormProvider>
  )
}
